// ─────────────────────────────────────────────
//  Firestore helpers — all DB operations live here
//  Collections: users, facilities, rooms, assignments,
//               breaks, alerts, messages, daily_boards,
//               audit_log, reports
// ─────────────────────────────────────────────
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc,
  updateDoc, deleteDoc, onSnapshot, query, where,
  orderBy, serverTimestamp, Timestamp, arrayUnion, arrayRemove, deleteField,
  writeBatch, runTransaction
} from "./sdk";
import { db, auth } from "./config";
import { staleImportIds } from "../domain/sheetImports";
import { STAFF_ALIASES_COLLECTION, buildAliasRecord } from "../domain/staffAliases";
import { assertHumanStaffCreation } from "../domain/staffCreation";
import { activeFacilityIds, CORE_FACILITY_IDS } from "../domain/facilities";
import { isTestFacilityId } from "../domain/testSession";

// ── AUDIT ACTOR STAMP ─────────────────────────
// Firestore triggers see the document, never the caller — so a write that
// should be audited (privilege change, staff↔login claim) has to carry WHO
// made it. `updatedBy` is that stamp; firestore.rules forces it to equal
// request.auth.uid, so the recorded actor cannot be forged by a bypassed
// client. Optional chaining keeps this safe under tests that mock ./config
// down to `{ db: {} }`.
const actorUid = () => auth?.currentUser?.uid || null;

// Adds the actor stamp to a patch when a signed-in user is making the write.
// Server-side jobs (admin SDK) bypass this path entirely.
const withActor = (data) => {
  const uid = actorUid();
  return uid ? { ...data, updatedBy: uid } : { ...data };
};

// NOTE: The admin "View as" testing mode is a CLIENT-SIDE visual role switch
// only. An admin keeps full write power while previewing (Firestore rules
// evaluate the real admin identity), so changes made in testing mode are saved
// for real. There is intentionally no write sandbox here.

// ── COLLECTION REFS ──────────────────────────
export const usersCol       = () => collection(db, "users");
export const staffCol       = () => collection(db, "staff");
export const facilitiesCol  = () => collection(db, "facilities");
export const roomsCol       = () => collection(db, "rooms");
export const assignmentsCol = () => collection(db, "assignments");
export const breaksCol      = () => collection(db, "breaks");
export const alertsCol      = () => collection(db, "alerts");
export const messagesCol    = () => collection(db, "messages");
export const tasksCol       = () => collection(db, "tasks");
export const boardsCol      = () => collection(db, "daily_boards");
export const auditCol       = () => collection(db, "audit_log");
export const reportsCol     = () => collection(db, "reports");
export const sheetImportsCol = () => collection(db, "sheet_imports");
export const orCasesCol     = () => collection(db, "or_cases");

// ── USERS ─────────────────────────────────────
export const getUser = (uid) =>
  getDoc(doc(db, "users", uid));

export const createUser = (uid, data) =>
  setDoc(doc(db, "users", uid), {
    ...data,
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });

export const updateUser = (uid, data) => {
  // withActor: role / isAIC / facilityIds changes are audited by the
  // auditUserChanges trigger, which reads this stamp for actorId.
  return updateDoc(doc(db, "users", uid), withActor(data));
};

export const listenToUser = (uid, callback, onError) =>
  onSnapshot(doc(db, "users", uid), callback, onError);

// Presence heartbeat. Firebase Auth cannot tell you who currently has the app
// open, so the client says so: `lastSeenAt` while the app is in the foreground,
// plus `lastLoginAt` once per sign-in.
//
// Deliberately NOT routed through updateUser(): withActor() stamps updatedBy on
// every write, and a heartbeat is not an actor performing an action — stamping
// it would make the audit trail's actor field mean two different things. The
// self-update rule permits both fields (only role/isAIC/facilityIds are frozen),
// and the audit trigger is field-scoped so a heartbeat writes no audit entry.
export const touchPresence = (uid, { login = false } = {}) =>
  updateDoc(doc(db, "users", uid), {
    lastSeenAt: serverTimestamp(),
    ...(login ? { lastLoginAt: serverTimestamp() } : {}),
  });

// All users in a facility carrying the AIC designation. Used to notify the AIC
// when a supervising doctor changes an assignment.
export const getFacilityAICs = async (facilityId) => {
  const snap = await getDocs(
    query(
      usersCol(),
      where("facilityIds", "array-contains", facilityId),
      where("isAIC", "==", true)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ── FACILITIES ────────────────────────────────
export const getFacility = (facilityId) =>
  getDoc(doc(db, "facilities", facilityId));

export const listenToFacility = (facilityId, callback, onError) =>
  onSnapshot(doc(db, "facilities", facilityId), callback, onError);

// The same doc, subscribed WITH metadata changes — the control panel's
// connection canary.
//
// WHY THIS EXISTS SEPARATELY: `onSnapshot` without `includeMetadataChanges`
// does not fire on a metadata-only change. Offline persistence is enabled
// (config.js), so when the device loses the network the client keeps serving
// the whole board out of IndexedDB and `metadata.fromCache` flips to true —
// with NO callback delivered. A wall display waiting to be told something is
// wrong would wait forever while showing hours-old staffing on a
// normal-looking board.
//
// Subscribing with metadata changes makes those transitions observable.
// Metadata-only deliveries are not billed as document reads, so the canary
// costs one listener and no read volume. Do NOT switch `listenToFacility`
// itself to this — its consumers would re-render on every cache transition
// for no benefit.
export const listenToFacilityMeta = (facilityId, callback, onError) =>
  onSnapshot(
    doc(db, "facilities", facilityId),
    { includeMetadataChanges: true },
    callback,
    onError
  );

// Live list of every facility document. Merged with the known-facility seed
// list in useFacilities so the core facilities always appear even before a
// doc exists. Admin reads (Facility Settings).
export const listenToFacilities = (callback, onError) =>
  onSnapshot(facilitiesCol(), callback, onError);

// Create a brand-new facility doc (Add Facility modal). Locations and
// specialties start empty so the new facility is a clean shell. Admin-only.
//
// OPTIONAL FIELD — `geofence: { lat, lng, radiusMeters, address }`: the campus
// coordinates that fence this facility's Board Manager wall devices
// (components/GeofenceGate.js). Deliberately NOT defaulted here: the built-in
// FACILITY_PINS are AdventHealth campuses, so a new tenant's facility starts
// with no fence (the board loads and says so) until real coordinates are
// stored — never with a wrong one. Validated in firestore.rules and again in
// domain/providerMap.js parseGeofence.
export const createFacility = (facilityId, data) =>
  setDoc(doc(db, "facilities", facilityId), {
    isActive: true,
    locations: [],
    specialties: [],
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Delete a facility doc (Remove Facility). Rooms/assignments scoped to it stop
// appearing because every board query filters by facilityId. Admin-only.
export const deleteFacility = (facilityId) =>
  deleteDoc(doc(db, "facilities", facilityId));

// Persist the facility's editable location list. Admin-only per Firestore rules.
export const updateFacilityLocations = (facilityId, locations) =>
  updateDoc(doc(db, "facilities", facilityId), {
    locations,
    updatedAt: serverTimestamp(),
  });

// Generic facility-doc merge — used by the Facility Settings page to save the
// information section (name, address, contacts) and the specialties list.
// setDoc+merge so it also bootstraps the doc for an empty-shell facility that
// has never been written before. Admin-only per Firestore rules.
export const updateFacility = (facilityId, data) =>
  setDoc(
    doc(db, "facilities", facilityId),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true }
  );

// Activate / deactivate a facility (empty-shell management). Admin-only.
// Phase 5.4 — Active / Standby / Archived lifecycle. Writes the new
// facilityStatus AND keeps the legacy isActive boolean in sync so every
// pre-5.4 consumer (useFacilities, pickers) keeps working unchanged.
export const setFacilityStatus = (facilityId, status) =>
  setDoc(
    doc(db, "facilities", facilityId),
    {
      facilityStatus: status,
      isActive: status === "active",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

export const setFacilityActive = (facilityId, isActive) =>
  updateFacility(facilityId, { isActive: !!isActive });

// ── STAFF (shared across facilities) ──────────
// The anesthesia provider roster. Separate from the auth `users` collection:
// staff entries are people who can be assigned to rooms, whether or not they
// have a login yet (future enhancement: link a staff entry to a user account
// so they can edit their own contact info). Shared facility-wide — every
// facility draws from the same pool — so this listens to the whole collection.
export const listenToStaff = (callback, onError) =>
  onSnapshot(collection(db, "staff"), callback, onError);

export const addStaff = (data) => {
  // ⚠️ AN IMPORT MAY NOT CREATE A PERSON. The guard is here, in front of the
  // write, rather than only in the screens that used to offer it — a menu that
  // stops offering the wrong button is a copy change; this is the thing that
  // has to be impossible. See domain/staffCreation for the ruling and the
  // twenty-three duplicate colleagues behind it.
  assertHumanStaffCreation(data || {});
  return addDoc(collection(db, "staff"), {
    name: "", phone: "", role: "crna",
    // Contact email for the Team directory (v2 §1.1). "" when unknown — an
    // empty string is what the directory renders as "no email"; a MISSING key
    // would too, but writing it makes the field discoverable in the console
    // and in an export. `phone` above stays the canonical phone field; there is
    // deliberately no second `phoneNumber` here (see domain/contact.js).
    email: "",
    // Directory opt-out flag (v2 §1.5). Written true so the field exists on
    // every new record; ABSENT still means listed, so records created before
    // this default are unaffected. Only `listed === false` hides a row.
    listed: true,
    primaryFacilityId: null, employmentType: "permanent",
    uid: null, // links to a login account (users/{uid}) when the person signs in
    ...data,
    // Who brought this person into existence. The unattended job has no uid —
    // and no longer has a path here at all.
    createdBy: actorUid(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateStaff = (staffId, data) =>
  // The self-link claim ({ uid }) rides this path, so the rule's field
  // allowlist covers exactly these three keys — see firestore.rules /staff.
  updateDoc(doc(db, "staff", staffId), withActor({ ...data, updatedAt: serverTimestamp() }));

export const removeStaff = (staffId) =>
  deleteDoc(doc(db, "staff", staffId));

// ── SELF-SERVICE CONTACT MIRROR ───────────────
// A person edits their phone/email on their PROFILE (users/{uid}); the Team
// directory reads `staff`. Without this mirror the directory keeps showing the
// number an administrator typed months ago while the person is certain they
// updated it — the exact failure a second phone field would cause, arriving by
// a different door.
//
// ⚠️ THE FIELD LIST HERE IS A CONTRACT WITH firestore.rules. The /staff update
// rule lets a non-admin write ONLY `phone`, `email`, `updatedAt` and
// `updatedBy`, and ONLY on a record whose stored `uid` is already theirs.
// Adding a key here (name, shortCode, listed…) does not widen anything — it
// makes the whole write fail with PERMISSION_DENIED, while every unit test
// keeps passing. Widen the rule first, deliberately, or not at all.
//
// Matched on `staff.uid`, never on name: a name match would let a rename
// silently redirect somebody's contact details onto a colleague's record.
// Returns the number of staff records updated (0 = this login has no linked
// staff record yet, which is normal and not an error).
export const mirrorContactToStaff = async (uid, contact) => {
  if (!uid) return 0;
  const snap = await getDocs(query(collection(db, "staff"), where("uid", "==", uid)));
  let updated = 0;
  for (const d of snap.docs) {
    await updateDoc(d.ref, withActor({
      phone: contact?.phone ?? "",
      email: contact?.email ?? "",
      updatedAt: serverTimestamp(),
    }));
    updated += 1;
  }
  return updated;
};

// Mirror a profile's ROLE onto its linked staff record.
//
// ⚠️ WITHOUT THIS, A ROLE CHANGE NEVER REACHES THE BOARD OR THE DIRECTORY.
// Setup → Users writes `users.subRole`; the Team directory buckets by
// `staff.role`, and the board's assignable pool reads the same field. They are
// different fields in different collections, and until 2026-08-20 nothing
// carried the edit across — nine people were filed under the wrong heading,
// eight of them from one afternoon's crna→aa correction.
//
// Matched on `staff.uid`, never on name — same rule as the contact mirror, and
// for the same reason: a rename must not redirect somebody's role onto a
// colleague's record. `staffRole` of null means "this profile is not a
// rosterable person" (an admin, a scheduler, a wall device) and writes nothing.
// Returns the number of staff records updated.
export const mirrorRoleToStaff = async (uid, staffRole) => {
  if (!uid || !staffRole) return 0;
  const snap = await getDocs(query(collection(db, "staff"), where("uid", "==", uid)));
  let updated = 0;
  for (const d of snap.docs) {
    if ((d.data()?.role || "").toLowerCase() === staffRole) continue; // already right
    await updateDoc(d.ref, withActor({ role: staffRole, updatedAt: serverTimestamp() }));
    updated += 1;
  }
  return updated;
};

// ── SURGEONS (Team directory v2 §1.3) ─────────
// ATC's own surgeon contact records — name, phone, email, specialty — owned and
// edited in-app by an administrator, one DOCUMENT per surgeon.
//
// ⚠️ THIS COLLECTION IS AUTHORITATIVE FOR WHO THE SURGEONS ARE. It used to be
// one of THREE surgeon lists; it is now the only one anybody edits.
//
//   surgeons/{id}                       ← AUTHORITATIVE. The roster: name,
//                                         phone, email, specialty, listed.
//                                         Written by setupPage.surgeons.js.
//   procedure_refs/{fid}.surgeons[]     ← DERIVED. A flat array of NAMES and
//                                         nothing else: the AssignmentGrid's
//                                         typeahead. The panel mirrors every
//                                         name into it with arrayUnion /
//                                         arrayRemove (addProcedureRef /
//                                         removeProcedureRef) so case entry
//                                         keeps working untouched, and the
//                                         grid's own "typed-and-remembered"
//                                         additions survive because those two
//                                         writes are surgical, never wholesale.
//   procedure_refs/{fid}.surgeonList[]  ← RETIRED. {name, phone, specialty}
//                                         rows, the old Setup panel's store.
//                                         NOTHING WRITES IT ANY MORE. It is
//                                         still READ once, by
//                                         getLegacySurgeonRoster() below, to
//                                         offer a one-click import of whatever
//                                         a facility already had — otherwise
//                                         retiring the field would silently
//                                         delete an admin's surgeon roster from
//                                         the screen that used to show it.
//
// WHY THE COLLECTION WON: an array field on a facility-scoped scheduling doc
// cannot carry a per-record `listed` opt-out, cannot be gated separately from
// the board data every signed-in account reads, and is rewritten wholesale on
// every edit (two admins editing at once, last write deletes the other's).
//
// ⚠️ THE PRICE, STATED: /surgeons writes are `isAdmin()` in firestore.rules,
// while the old panel let an in-facility AIC edit the list too. An AIC can
// still put a surgeon's NAME on the picker by typing it into a case (the
// typed-and-remembered path), but the directory record — phone, email,
// specialty — is an administrator's to keep. The panel says so on screen.
//
// Shape mirrors the `staff` helpers exactly, including the withActor() stamp —
// firestore.rules verifies `updatedBy == request.auth.uid` (honestActor) so the
// audit actor on a directory edit cannot be forged.
export const surgeonsCol = () => collection(db, "surgeons");

export const listenToSurgeons = (callback, onError) =>
  onSnapshot(collection(db, "surgeons"), callback, onError);

export const addSurgeon = (data) =>
  addDoc(collection(db, "surgeons"), withActor({
    name: "",
    // `phoneNumber` + `phoneDisplay`, NOT `phone`. This is a new collection
    // with no legacy key, so it stores the contract's own names: digits to
    // dial, and the formatted string a human reads. domain/contact.js reads
    // both shapes so callers never branch on which kind of record they hold.
    phoneNumber: "",
    phoneDisplay: "",
    email: "",
    specialty: "",
    facilityId: null, // which facility maintains this record
    listed: true,     // absent still means listed — see domain/contact.isListed
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));

export const updateSurgeon = (surgeonId, data) =>
  updateDoc(doc(db, "surgeons", surgeonId), withActor({ ...data, updatedAt: serverTimestamp() }));

export const removeSurgeon = (surgeonId) =>
  deleteDoc(doc(db, "surgeons", surgeonId));

// Every surgeon record a facility maintains, once. Used by the Add Facility
// modal's "copy surgeons from" — which copies the RECORDS now, not the retired
// array field, so a cloned facility gets a real directory rather than rows the
// directory cannot see.
export const listSurgeonsForFacility = async (facilityId) => {
  if (!facilityId) return [];
  const snap = await getDocs(query(collection(db, "surgeons"), where("facilityId", "==", facilityId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ONE-SHOT READ OF THE RETIRED LIST, and the only remaining reader of it.
// Returns whatever a facility's procedure_refs doc still holds as rich rows —
// `surgeonList[]` if it is there, otherwise the flat `surgeons[]` names lifted
// into the same shape — so the Setup panel can offer "import the N surgeons
// this facility already had". Never written back.
export const getLegacySurgeonRoster = async (facilityId) => {
  if (!facilityId) return [];
  const snap = await getDoc(doc(db, "procedure_refs", facilityId));
  const data = snap.exists() ? snap.data() : null;
  if (Array.isArray(data?.surgeonList)) {
    return data.surgeonList.filter((sg) => sg && typeof sg.name === "string" && sg.name.trim());
  }
  if (Array.isArray(data?.surgeons)) {
    return data.surgeons
      .filter((n) => typeof n === "string" && n.trim())
      .map((n) => ({ name: n, phone: "", specialty: "" }));
  }
  return [];
};

// ── TEAM DIRECTORY — no listener here, on purpose ──
// listenToTeamDirectory used to read ONE document, `team_directory/current`,
// mirrored weekly out of the department's link-shared Google Sheet. Retired
// 2026-08-19: the Team page now composes /staff, /surgeons and /facilities,
// which ATC owns and admins maintain. See useTeamDirectory.

// ── SHEET IMPORTS (recent-imports history) ────
// Each doc records ONE applied assignment-sheet import: the date-stamped
// filename, its source (manual-excel | manual-sheet | scheduled), and the parsed
// draft snapshot (JSON string, so nested arrays sidestep Firestore's array-of-
// array limit). Powers the "recent files" list under the Import section and lets
// the AIC re-apply a recent sheet in one click. Kept small by pruneStaleSheetImports.
export const saveSheetImport = (facilityId, dateKey, { filename, source, snapshot }) =>
  addDoc(sheetImportsCol(), {
    facilityId: facilityId || "default",
    dateKey: dateKey || "",
    filename: filename || "",
    source: source || "manual-excel",
    snapshotJson: JSON.stringify(snapshot ?? {}),
    importedAt: serverTimestamp(),
  });

// Live subscription to a facility's import history. NO orderBy in the query (so
// it needs no composite index — the collection is tiny and pruned to 3 days);
// callers sort/slice client-side via domain/sheetImports.selectRecent.
export const listenToSheetImports = (facilityId, callback, onError) =>
  onSnapshot(
    query(sheetImportsCol(), where("facilityId", "==", facilityId || "default")),
    callback,
    onError
  );

export const deleteSheetImport = (id) =>
  deleteDoc(doc(db, "sheet_imports", id));

// Backfill a legacy record's immutable snapshot (one-time, from the record's
// own dated Drive file) so future Restores never touch the live API again.
export const backfillSheetImportSnapshot = (id, snapshot) =>
  updateDoc(doc(db, "sheet_imports", id), { snapshotJson: JSON.stringify(snapshot ?? {}) });

// Cleanup task: delete every import older than `days` for a facility. Called on
// the Daily List mount (client-side, admin/AIC context) so no scheduled Cloud
// Function / Blaze plan is needed. Returns the number of docs removed.
export const pruneStaleSheetImports = async (facilityId, nowMs, days = 3) => {
  const snap = await getDocs(
    query(sheetImportsCol(), where("facilityId", "==", facilityId || "default"))
  );
  const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const ids = staleImportIds(list, nowMs, days);
  await Promise.all(ids.map((id) => deleteSheetImport(id)));
  return ids.length;
};

// Sources that belong to the GLOBAL import history (vs the facility-only one).
const GLOBAL_IMPORT_SOURCES = new Set(["global-sheet", "scheduled", "scheduled-internal"]);

// Count-based retention: each history list (global vs facility-only) keeps its
// 3 newest snapshot records; older ones are deleted when a new import saves.
export const pruneSheetImportsKeepLast = async (facilityId, keep = 3) => {
  const snap = await getDocs(
    query(sheetImportsCol(), where("facilityId", "==", facilityId || "default"))
  );
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const ts = (r) => r.importedAt?.seconds || 0;
  const lists = [
    all.filter((r) => GLOBAL_IMPORT_SOURCES.has(r.source)),
    all.filter((r) => !GLOBAL_IMPORT_SOURCES.has(r.source)),
  ];
  const doomed = lists.flatMap((list) =>
    list.sort((a, b) => ts(b) - ts(a)).slice(keep).map((r) => r.id)
  );
  await Promise.all(doomed.map((id) => deleteSheetImport(id)));
  return doomed.length;
};

// ── STAFF ALIASES (import identity, confirmed by a human) ─────────────
//
// One document per raw sheet string a person bound to a staff record, so the
// importer stops asking the same question every morning. See
// src/domain/staffAliases.js for the shape and WHY it is facility-scoped, and
// firestore.rules for who may write one (admin / AIC — never an unattended job,
// which is the whole point).
//
// ⚠️ setDoc with a DERIVED id, not addDoc. Confirming the same name twice must
// correct the one record, not leave two disagreeing answers for the resolver to
// choose between — and "choose between" is the thing this feature exists to
// abolish.
export const staffAliasesCol = () => collection(db, STAFF_ALIASES_COLLECTION);

export const listStaffAliases = async (facilityId) => {
  if (!facilityId) return [];
  const snap = await getDocs(query(staffAliasesCol(), where("facilityId", "==", facilityId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const listenToStaffAliases = (facilityId, callback, onError) =>
  onSnapshot(
    query(staffAliasesCol(), where("facilityId", "==", facilityId || "default")),
    callback,
    onError
  );

// Write one confirmation. Returns { ok, reason, id } — never throws on a
// malformed input, because this runs behind a click in a review list and losing
// the reviewer's pass over 15 items to an exception is worse than one refusal.
export const saveStaffAlias = async ({ raw, staffId, facilityId, source } = {}) => {
  const built = buildAliasRecord({
    raw, staffId, facilityId, source, confirmedBy: actorUid(),
  });
  if (!built.ok) return built;
  await setDoc(doc(db, STAFF_ALIASES_COLLECTION, built.id), {
    ...built.data,
    confirmedAt: serverTimestamp(),
  });
  return { ok: true, reason: "", id: built.id };
};

export const deleteStaffAlias = (id) => deleteDoc(doc(db, STAFF_ALIASES_COLLECTION, id));

// ── ROOMS ─────────────────────────────────────
export const listenToRooms = (facilityId, callback, onError) =>
  onSnapshot(
    query(roomsCol(), where("facilityId", "==", facilityId)),
    callback,
    onError
  );

export const createRoom = (data) => {
  return addDoc(roomsCol(), {
    status: "empty",
    providerId: null,
    supervisingDoctorId: null,
    comments: "",
    isActive: true,
    onBreak: false,
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const updateRoomStatus = (roomId, status, comment = "") => {
  return updateDoc(doc(db, "rooms", roomId), {
    status,
    comment,
    updatedAt: serverTimestamp(),
  });
};

// Write the current provider/supervisor onto the room doc so the board card
// shows it. The `assignments` collection keeps the schedule record; the room
// doc holds the *live* assignment the board renders from.
// Write ONLY the people on a room, leaving its status alone.
//
// Two reasons this is not setRoomAssignment:
//
// 1. setRoomAssignment hardcodes `status: "ready"`, so using it to change who is
//    in a RUNNING room silently un-runs it on the board.
// 2. The room doc and the assignment row are two separate writes, and a flow
//    that updates one but not the other leaves the board lying. RoomCard
//    resolves `asg.ahpName || room.providerName` — so a DELIBERATELY cleared
//    name on the row falls straight through the `||` to a stale room doc and
//    resurrects the person who was just relieved. A room that reads as staffed
//    and is not is the worst failure this board has.
//
// Pass explicit nulls/empties to clear. Callers that change staffing on a row
// must call this for the same room in the same action.
export const setRoomProvider = (roomId, data) =>
  updateDoc(doc(db, "rooms", roomId), {
    ...data,
    updatedAt: serverTimestamp(),
  });

export const setRoomAssignment = (roomId, data) =>
  updateDoc(doc(db, "rooms", roomId), {
    status: "ready",
    ...data,
    updatedAt: serverTimestamp(),
  });

// Clear a room back to empty — removes the live assignment, break state, and
// comments so the box reads as an open room on the board.
export const clearRoom = (roomId) =>
  updateDoc(doc(db, "rooms", roomId), {
    status: "empty",
    providerId: null, providerName: "", providerRole: "",
    supervisingDoctorId: null, supervisorName: "",
    scheduledStartTime: null, signInTime: null, signOutTime: null,
    comment: "", onBreak: false, breaks: null, relieverName: "",
    // ⚠️ THE WHOLE BREAK IDENTITY DIES WITH THE DAY, NOT JUST THE NAME.
    // `relieverName` was cleared and `relieverId` was not — and `iAmCover` is
    // derived from `relieverId` alone. So yesterday's cover kept a live break
    // control on a room with no break in it, and now that the arrival tap is
    // the privileged transition, a clean morning board would offer it to the
    // wrong person. `breakTimes` and the approved-break window are day-scoped
    // for the same reason (endRoomBreak already clears the window; the reset
    // never did).
    relieverId: null, breakTimes: null,
    breakChannel: null, breakStartedAt: null, breakMinutes: null, activeBreakId: null,
    // Day-scoped condition state dies with the day — a cleared room must not
    // carry yesterday's "Delayed start" chip or handoff trail into the new one.
    condition: null, followUp: null, handoffs: [],
    updatedAt: serverTimestamp(),
  });

export const signInToRoom = (roomId, providerId) => {
  // Write the canonical status "active" per C-002 / src/domain/status.js
  // ROOM_STATUSES (D-09). Forward-only fix: existing legacy "in_progress"
  // records are NOT backfilled here — normalizeStatus() already resolves them
  // on read, and the backfill is explicitly deferred to Phase 5.
  return updateDoc(doc(db, "rooms", roomId), {
    status: "active",
    providerId,
    signInTime: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const signOutOfRoom = (roomId) => {
  return updateDoc(doc(db, "rooms", roomId), {
    status: "ready",
    providerId: null,
    signOutTime: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// A doctor assigns a break-giver to cover one of the room's break channels, or
// that giver arrives and starts covering. The caller passes the
// already-transitioned breaks map (see domain/breaks assignCover /
// arriveForCover) so the state-machine rules stay in one place.
//
// ⚠️ `onBreak` IS A PARAMETER, NOT A CONSTANT. It used to be hardcoded true, so
// the room flipped to on-break the moment a cover was NAMED — outlining the card
// and, through coverage.js, declaring the on-call OB CRNA uncovered "for a
// break" before anybody had walked anywhere. It is true only once somebody is
// actually in the room.
//
// ⚠️ PASS `channel` TO WRITE A DOTTED PATH. Clients used to write the WHOLE
// four-channel map while the sheet engine writes `breaks.<ch>`, so a client
// write silently reverted whatever the engine had advanced in another channel.
// Splitting a break into three writes (assign → arrive → end) turns that from a
// rare race into a per-break hazard. `affectedKeys()` reports the TOP-LEVEL key
// for a dotted path, so firestore.rules' provider allowlist is unaffected.
export const setRoomBreak = (roomId, { breaks, channel, breakTimes, relieverId, relieverName, onBreak = true }) =>
  updateDoc(doc(db, "rooms", roomId), {
    ...(channel ? { [`breaks.${channel}`]: breaks[channel] } : { breaks }),
    // Omitted rather than nulled when the caller has no times: a room that has
    // never recorded one keeps no empty map, and old rooms stay untouched.
    ...(breakTimes
      ? (channel ? { [`breakTimes.${channel}`]: breakTimes[channel] } : { breakTimes })
      : {}),
    relieverId: relieverId || null,
    relieverName: relieverName || "",
    onBreak,
    updatedAt: serverTimestamp(),
  });

// End the active break: caller passes the completed breaks map (meals lock
// "done", bathroom resets to "none") and the reliever is cleared.
export const endRoomBreak = (roomId, { breaks, channel, breakTimes }) =>
  updateDoc(doc(db, "rooms", roomId), {
    ...(channel ? { [`breaks.${channel}`]: breaks[channel] } : { breaks }),
    ...(breakTimes
      ? (channel ? { [`breakTimes.${channel}`]: breakTimes[channel] } : { breakTimes })
      : {}),
    relieverId: null,
    relieverName: "",
    onBreak: false,
    // Phase 5.3 — close the approved-break countdown window (no-ops when the
    // break wasn't supervisor-approved).
    breakChannel: null,
    breakStartedAt: null,
    breakMinutes: null,
    activeBreakId: null,
    updatedAt: serverTimestamp(),
  });

// ── ROOM CONDITIONS + PROVIDER HANDOFF (Phase 5.2) ──
// `condition` and `followUp` are SEPARATE fields from the canonical room
// status (C-002) — none of these helpers ever touch `status`.

// Post/update the room's condition. `condition` comes pre-validated from
// domain/conditions.makeCondition (kind + capped note + setBy).
export const setRoomCondition = (roomId, condition) =>
  updateDoc(doc(db, "rooms", roomId), {
    condition,
    updatedAt: serverTimestamp(),
  });

// Either party (CRNA or supervising doctor) clears the condition.
export const clearRoomCondition = (roomId) =>
  updateDoc(doc(db, "rooms", roomId), {
    condition: null,
    updatedAt: serverTimestamp(),
  });

// Stage a handoff: origin shows "Following to X", destination shows the
// "Awaiting arrival" standby look. Both halves come from
// domain/conditions.makeFollowUpPair; written atomically.
export const setRoomFollowUp = (fromRoomId, toRoomId, { origin, destination }) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "rooms", fromRoomId), { followUp: origin, updatedAt: serverTimestamp() });
  batch.update(doc(db, "rooms", toRoomId), { followUp: destination, updatedAt: serverTimestamp() });
  return batch.commit();
};

// Cancel a staged handoff (both halves cleared).
export const clearRoomFollowUp = (fromRoomId, toRoomId) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "rooms", fromRoomId), { followUp: null, updatedAt: serverTimestamp() });
  if (toRoomId && toRoomId !== fromRoomId) {
    batch.update(doc(db, "rooms", toRoomId), { followUp: null, updatedAt: serverTimestamp() });
  }
  return batch.commit();
};

// Mark the provider arrived: destination converts to normal display and the
// hop is appended to the destination's `handoffs` trail (the shift room-chain,
// see domain/conditions.roomChainForProvider). `entry` from makeHandoffEntry.
export const markFollowUpArrived = (fromRoomId, toRoomId, entry) => {
  const batch = writeBatch(db);
  if (fromRoomId) {
    batch.update(doc(db, "rooms", fromRoomId), { followUp: null, updatedAt: serverTimestamp() });
  }
  batch.update(doc(db, "rooms", toRoomId), {
    followUp: null,
    handoffs: arrayUnion(entry),
    updatedAt: serverTimestamp(),
  });
  return batch.commit();
};

// ── DAILY BOARD ───────────────────────────────
const boardId = (facilityId, date) =>
  `${facilityId}_${date}`;

export const getDailyBoard = (facilityId, date) =>
  getDoc(doc(db, "daily_boards", boardId(facilityId, date)));

export const listenToDailyBoard = (facilityId, date, callback) =>
  onSnapshot(
    doc(db, "daily_boards", boardId(facilityId, date)),
    callback
  );

export const createDailyBoard = (facilityId, date, aicId) => {
  return setDoc(doc(db, "daily_boards", boardId(facilityId, date)), {
    facilityId,
    date,
    aicId,
    roomOrder: [],
    notes: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// Upsert the day's provider roster (AIC + supervising doctors + CRNAs) onto the
// daily board doc. Merge so an existing board's roomOrder/notes are preserved.
// Editable by doctors/AIC/admin (see firestore.rules). The roster for *today*
// is the source of truth for who the active AIC is (see AuthContext), so the
// board never has to write the persistent isAIC flag on user docs.
export const setDailyRoster = (facilityId, date, data, updatedBy) =>
  setDoc(
    doc(db, "daily_boards", boardId(facilityId, date)),
    { facilityId, date, ...data, updatedBy, updatedAt: serverTimestamp() },
    { merge: true }
  );

// ── ASSIGNMENTS ───────────────────────────────
export const listenToAssignments = (facilityId, date, callback) =>
  onSnapshot(
    query(
      assignmentsCol(),
      where("facilityId", "==", facilityId),
      where("date", "==", date)
    ),
    callback
  );

// One-shot read of a facility's day, for callers that need the answer once and
// must not hold a live subscription open (the sign-in facility resolver reads
// every facility the user can access — subscribing to all of them would leak a
// listener per campus on every login).
export const getAssignmentsOnce = async (facilityId, date) => {
  const snap = await getDocs(
    query(
      assignmentsCol(),
      where("facilityId", "==", facilityId),
      where("date", "==", date)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const createAssignment = (data) => {
  return addDoc(assignmentsCol(), {
    ...data,
    status: "scheduled",
    createdAt: serverTimestamp(),
  });
};

// ── OR CASES (Room Status, 2026-09-07) ────────
//
// One document per CASE, not per room: a room runs several cases a day and each
// one keeps its own record so turnover and case duration stay answerable. The
// previous case is CLOSED, never reset — see docs/specs/2026-09-07-mobile-room-
// status-design.md §4.2.
//
// ⚠️ `phases` IS A MAP, NOT AN ARRAY. `phases.induction.ackAt` is then an
// independent field path, so the CRNA stamping a phase and the supervising MD
// acknowledging it can never read-modify-write the same array and clobber one
// another. With an array, two taps a second apart lose one of the two.
//
// ⚠️ DAY-SCOPED BY THE FACILITY'S CLOCK. `date` must come from
// `dayKey(new Date(), facilityTz(facility))` — never `rosterDateKey`, which is
// the DEVICE's calendar date. Two day-key definitions still coexist in this
// codebase; a document written under the device key on a mis-set device files
// where no client queries, with no error and no empty state.
export const listenToCases = (facilityId, date, callback, onError) =>
  onSnapshot(
    query(
      orCasesCol(),
      where("facilityId", "==", facilityId),
      where("date", "==", date)
    ),
    callback,
    onError
  );

// ⚠️ `phases` IS DESTRUCTURED, NOT SPREAD-THEN-OVERWRITTEN. It used to sit
// after `...data` as a hardcoded `{}`, so a caller that passed phases had them
// silently discarded. That matters because opening a case WITH its first phase
// already stamped is one atomic write — the alternative is create, read back the
// new id, then update, which can leave a case open with nothing in it if the
// second write fails. Defaults to `{}`, so every existing caller is unchanged.
export const createCase = ({ phases = {}, ...data }) =>
  addDoc(orCasesCol(), {
    ...data,
    state: "open",
    phases,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Stamp one field path on a case — a phase time, an acknowledgement, timeIn or
// timeOut. The caller passes an already-built dotted patch so this never has to
// know which of the two roles is writing; the RULES decide that, not this
// function.
export const stampCasePhase = (caseId, patch, updatedBy = null) =>
  updateDoc(doc(db, "or_cases", caseId), {
    ...patch,
    updatedAt: serverTimestamp(),
    ...(updatedBy ? { updatedBy } : null),
  });

// Close the open case. `timeOut` is stamped here only when it is still missing,
// so pressing "New case" without having pressed "Time out" can never leave a
// case open forever — but a real Time out tap is never overwritten.
//
// ⚠️ AN INFERRED TIME OUT IS FLAGGED AS ONE. A timeOut written here equals the
// NEXT case's start, not the moment the patient actually left the room. These
// documents are the record that answers case duration and turnover, so storing
// an inferred value as if it were observed would quietly corrupt both numbers.
// `timeOutInferred: true` marks it; any duration report must exclude it rather
// than average it in. Never show an inferred time to a clinician as observed.
export const closeCase = (caseId, { stampTimeOut = false } = {}) =>
  updateDoc(doc(db, "or_cases", caseId), {
    state: "closed",
    ...(stampTimeOut ? { timeOut: serverTimestamp(), timeOutInferred: true } : null),
    updatedAt: serverTimestamp(),
  });

// ⚠️ STATUS ONLY — DO NOT REACH FOR `updateRoomStatus` FROM A PHASE STAMP.
// `updateRoomStatus` takes `comment = ""` and writes it unconditionally, so
// calling it to move a room from Induction to Closing would silently WIPE the
// CRNA's room comment on every tap. This writes the status and nothing else.
//
// Still rules-legal for a provider: firestore.rules field-scopes isProvider()
// with hasOnly(['status','comment','updatedAt']), and {status, updatedAt} is a
// subset of that set.
//
// And it is deliberately NOT setRoomAssignment, which hardcodes status:"ready"
// and would un-run a live room (see the note above setRoomProvider).
export const setRoomStatusOnly = (roomId, status) =>
  updateDoc(doc(db, "rooms", roomId), {
    status,
    updatedAt: serverTimestamp(),
  });

// ── INTERNAL SCHEDULE (scheduling module) ─────
// Staged next-day rows (draft-row shape) the midnight import reads when the
// internal-schedule toggle is ON. Admin/Scheduler only (rules-enforced).
export const listenToScheduleRows = (facilityId, date, callback, onError) =>
  onSnapshot(
    query(
      collection(db, "schedule_rows"),
      where("facilityId", "==", facilityId),
      where("date", "==", date)
    ),
    callback,
    onError
  );

export const addScheduleRow = (data) =>
  addDoc(collection(db, "schedule_rows"), { ...data, updatedAt: serverTimestamp() });

export const updateScheduleRow = (rowId, data) =>
  updateDoc(doc(db, "schedule_rows", rowId), { ...data, updatedAt: serverTimestamp() });

export const deleteScheduleRow = (rowId) =>
  deleteDoc(doc(db, "schedule_rows", rowId));

// Every assignment write records WHO and WHEN.
//
// Without this there is no accountability and no possibility of undo: the board
// cannot answer "who moved OR 6", and a wrong staffing change is indistinguishable
// from a right one after the fact. `domain/audit.js` documents assignment/room/
// break auditing as a deliberate gap filled by nothing else, so the cheapest
// honest fix is to stamp the row itself.
//
// `updatedBy` is verified against the caller in firestore.rules (honestActor),
// because a stamp a client can forge is worse than no stamp — it looks like
// accountability while being none. Callers may still pass their own updatedAt
// (the sheet import does) and it is not overridden.
export const updateAssignment = (assignmentId, data) => {
  // withActor(), not a hand-rolled stamp: it OMITS updatedBy when nobody is
  // signed in rather than writing null, which matters because firestore.rules
  // compares the field to request.auth.uid — a null would fail the honest-actor
  // check and deny an otherwise legitimate server-side write.
  return updateDoc(doc(db, "assignments", assignmentId), {
    updatedAt: serverTimestamp(),
    ...withActor(data),
  });
};

// Grid-row edit path for the Phase 4 assignment table. Named alias so call
// sites read as "edit a row of the daily grid" rather than a generic update.
export const updateAssignmentRow = (assignmentId, data) =>
  updateAssignment(assignmentId, data);

// Grid-row removal path for empty OR rows (Gap 5). Deletes the assignment doc
// entirely — the grid re-renders without it on the next snapshot.
export const deleteAssignmentRow = (assignmentId) =>
  deleteDoc(doc(db, "assignments", assignmentId));

// Hide / unhide a template OR room from the day's grid (Gap 5b — doc-less rows).
// Persisted as hiddenRooms[] on the daily_boards doc via Firestore array sentinels
// so concurrent users see the same set of visible rooms without a read-modify-write.
export const hideTemplateRoom = (facilityId, date, room) =>
  updateDoc(doc(db, "daily_boards", boardId(facilityId, date)), {
    hiddenRooms: arrayUnion(room),
    updatedAt: serverTimestamp(),
  });

export const unhideTemplateRoom = (facilityId, date, room) =>
  updateDoc(doc(db, "daily_boards", boardId(facilityId, date)), {
    hiddenRooms: arrayRemove(room),
    updatedAt: serverTimestamp(),
  });

// ── PROCEDURE REFS ────────────────────────────
// "Typed-and-remembered" autocomplete bag (Phase 4 D-01b): one doc per
// facility at procedure_refs/{facilityId} holding distinct surgeons[] and
// procedures[] entered in any previous assignment row.
// Live subscription — the scheduling module's surgeon/procedure dropdowns
// update the moment a new value is remembered anywhere in the app.
// specialties[] + proceduresBySpecialty{} drive the specialty-first picker.
// One-shot read of a facility's configured surgeons, for the import paths —
// they run once and have no use for a live subscription.
export const getFacilitySurgeons = async (facilityId) => {
  if (!facilityId) return [];
  try {
    const snap = await getDoc(doc(db, "procedure_refs", facilityId));
    const list = snap.exists() ? snap.data()?.surgeons : null;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    // An unreadable list must never fail an import: the cell is then written
    // exactly as the sheet had it, which is the pre-existing behaviour.
    console.warn("[procedure_refs] surgeons unreadable:", err?.message);
    return [];
  }
};

export const listenToProcedureRefs = (facilityId, callback, onError) =>
  onSnapshot(
    doc(db, "procedure_refs", facilityId),
    (snap) => {
      const data = snap.exists() ? snap.data() : null;
      callback({
        surgeons: data?.surgeons ?? [],
        procedures: data?.procedures ?? [],
        specialties: data?.specialties ?? [],
        proceduresBySpecialty: data?.proceduresBySpecialty ?? {},
      });
    },
    onError
  );

// Remember a procedure UNDER a specialty (and in the flat list for the legacy
// datalist). arrayUnion keeps both writes race-free and idempotent.
export const addSpecialtyProcedure = (facilityId, specialty, procedure) => {
  const s = String(specialty || "").trim();
  const p = String(procedure || "").trim();
  if (!s || !p) return Promise.resolve(null);
  return setDoc(
    doc(db, "procedure_refs", facilityId),
    {
      specialties: arrayUnion(s),
      proceduresBySpecialty: { [s]: arrayUnion(p) },
      procedures: arrayUnion(p),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const addSpecialty = (facilityId, specialty) => {
  const s = String(specialty || "").trim();
  if (!s) return Promise.resolve(null);
  return setDoc(
    doc(db, "procedure_refs", facilityId),
    { specialties: arrayUnion(s), updatedAt: serverTimestamp() },
    { merge: true }
  );
};

// ── TRANSFERS (provider traveling between facilities) ──
// One doc per in-transit provider. facilityIds carries BOTH ends so a single
// array-contains query lights up the Traveling section on both boards.
export const createTransfer = (data) =>
  addDoc(collection(db, "transfers"), {
    status: "traveling",
    createdAt: serverTimestamp(),
    ...data,
    facilityIds: [data.fromFacilityId, data.toFacilityId].filter(Boolean),
  });

export const listenToTransfers = (facilityId, date, callback, onError) =>
  onSnapshot(
    query(
      collection(db, "transfers"),
      where("facilityIds", "array-contains", facilityId),
      where("date", "==", date),
      where("status", "==", "traveling")
    ),
    callback,
    onError
  );

export const updateTransfer = (transferId, data) =>
  updateDoc(doc(db, "transfers", transferId), data);

// ── VOICE CONTROL ─────────────────────────────
// Audit log: every voice command (transcript, parsed intent, what was proposed,
// what the human decided). Append-only from clients.
export const logVoiceCommand = (data) =>
  addDoc(collection(db, "voice_logs"), { ...data, createdAt: serverTimestamp() });

// Admin bug/issue reports spoken in the app — the dev loop consumes these.
export const createVoiceReport = (data) =>
  addDoc(collection(db, "voice_reports"), { ...data, status: "open", createdAt: serverTimestamp() });

// Delete every still-traveling transfer for a date — used by the day-reset
// paths (Clear Roster) so the Traveling section empties with the board.
export const clearTravelingTransfers = async (date) => {
  const snap = await getDocs(
    query(
      collection(db, "transfers"),
      where("date", "==", date),
      where("status", "==", "traveling")
    )
  );
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  return snap.size;
};

// ── SCHEDULE DAY STATE (Close / Reopen) ───────
// One doc per date: { closed, closedAt, closedBy }. Closing finalizes the
// internal schedule for that date across every facility.
export const listenToScheduleDay = (date, callback, onError) =>
  onSnapshot(
    doc(db, "schedule_days", date),
    (snap) => callback(snap.exists() ? snap.data() : { closed: false }),
    onError
  );

export const setScheduleDayClosed = (date, closed, uid) =>
  setDoc(
    doc(db, "schedule_days", date),
    { closed, closedAt: serverTimestamp(), closedBy: uid || null },
    { merge: true }
  );

export const listProcedureRefs = async (facilityId) => {
  const snap = await getDoc(doc(db, "procedure_refs", facilityId));
  const data = snap.exists() ? snap.data() : null;
  return {
    surgeons: data?.surgeons ?? [],
    procedures: data?.procedures ?? [],
  };
};

// Append a distinct remembered value. arrayUnion is atomic server-side — no
// read-modify-write race when two doctors save rows at the same time.
export const addProcedureRef = (facilityId, kind, value) => {
  const v = typeof value === "string" ? value.trim() : "";
  if (!v) return Promise.resolve(null); // never persist blanks
  if (kind !== "surgeons" && kind !== "procedures") {
    return Promise.reject(new Error(`Unknown procedure_ref kind: ${kind}`));
  }
  return setDoc(
    doc(db, "procedure_refs", facilityId),
    { [kind]: arrayUnion(v), updatedAt: serverTimestamp() },
    { merge: true }
  );
};

// Remove a remembered value. Same kind validation + blank guard as addProcedureRef.
export const removeProcedureRef = (facilityId, kind, value) => {
  const v = typeof value === "string" ? value.trim() : "";
  if (!v) return Promise.resolve(null);
  if (kind !== "surgeons" && kind !== "procedures") {
    return Promise.reject(new Error(`Unknown procedure_ref kind: ${kind}`));
  }
  return setDoc(
    doc(db, "procedure_refs", facilityId),
    { [kind]: arrayRemove(v), updatedAt: serverTimestamp() },
    { merge: true }
  );
};

// ⚠️ THERE IS NO setSurgeonList() ANY MORE, AND ITS ABSENCE IS THE POINT.
// It wrote `procedure_refs/{facilityId}.surgeonList[]` — the rich surgeon rows
// the old Setup panel owned — as a WHOLESALE array replacement. The surgeon
// roster lives in the `surgeons` collection now (one document each, see the
// SURGEONS block near the top of this file); `surgeons[]` on this doc is a
// DERIVED name cache for the AssignmentGrid typeahead, kept in step by the
// surgical addProcedureRef / removeProcedureRef above.
//
// If a wholesale writer comes back, the "typed-and-remembered" names a doctor
// added from a case row go with it.

// ── CROSS-FACILITY STAFF (D-09) ───────────────
// Everyone whose facilityIds includes the given facility — used by the
// cross-facility picker and the per-room reassignment dropdowns. Parameterized
// query builders only (no string interpolation of user data).
export const listFacilityStaff = async (facilityId) => {
  const snap = await getDocs(
    query(usersCol(), where("facilityIds", "array-contains", facilityId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ── THE PEOPLE AT A CAMPUS, FOR THE CROSS-FACILITY PICKER ────────────────────
//
// ⚠️ `listFacilityStaff` ABOVE READS `users`, AND THAT IS WHY THE PICKER WAS
// BROKEN FOR EVERYONE EXCEPT AN ADMIN. `firestore.rules` allows a users read
// only for `request.auth.uid == uid || isAdmin()`, so a LIST query is
// admin-only. Reproduced against production on 2026-08-24:
//
//     admin            -> allowed
//     anesthesiologist -> permission-denied
//     CRNA             -> permission-denied
//
// and the modal reported "Couldn't load the roster. Check your connection" — a
// refusal dressed as a network fault, which is the failure mode this repo has a
// standing rule about.
//
// ⚠️ AND IT FOUND NOBODY EVEN FOR AN ADMIN. `users` models ACCOUNTS: 216 of 218
// real docs carry `facilityIds: ["default"]`, with ONE each for tampa and
// carrollwood. The people who actually work at a campus live in `staff`, keyed
// by `primaryFacilityId` (default 222, tampa 5). The picker excludes anyone
// already at the current facility, so searching the others returned an empty
// list by construction.
//
// ⚠️ AND THE ID IT HANDED BACK WAS THE WRONG KIND. A roster member is a STAFF
// id everywhere else on this board; the old path wrote a `users` doc id (a uid)
// into `roster.crnas`, which nothing downstream resolves.
//
// So: read `staff`, which clinical roles may list, and hand back the shape the
// picker already consumes — `role` in the users vocabulary it branches on, and
// a synthetic `facilityIds` so its existing "already here" filter is untouched.
// ── ACCOUNTS THAT ARE NOT YET PEOPLE ─────────
//
// ⚠️ THE OWNER'S RULE IS "ALL USERS ARE STAFF", AND THE DATA DISAGREED. `users`
// models LOGINS and `staff` models PEOPLE; creating an account never created the
// person, so a clinician could sign in and be invisible to every picker on the
// board. Reported live on 2026-09-11 with a doctor who could not be put on the
// day's board at all.
//
// This returns the accounts for a facility that have NO directory record yet, so
// a picker can offer them and create the record on the spot.
//
// ⚠️ ADMIN-ONLY, AND IT MUST FAIL SOFT. `firestore.rules` guards `users` with a
// per-document read (`request.auth.uid == uid || isAdmin()`), and the uid half
// cannot be satisfied by a LIST — so this query is `permission-denied` for every
// doctor and CRNA. That is exactly how this same call once broke "Add Staff from
// Another Facility" for every clinician and reported it as a network fault. The
// caller must treat a throw as "no extra names to offer", never as an error.
export const listAccountsWithoutStaff = async (facilityId) => {
  const [userSnap, staffSnap] = await Promise.all([
    getDocs(query(collection(db, "users"), where("facilityIds", "array-contains", facilityId))),
    getDocs(query(staffCol(), where("primaryFacilityId", "==", facilityId))),
  ]);
  const linkedUids = new Set();
  const linkedEmails = new Set();
  for (const d of staffSnap.docs) {
    const st = d.data() || {};
    if (st.uid) linkedUids.add(st.uid);
    const em = String(st.email || "").trim().toLowerCase();
    if (em) linkedEmails.add(em);
  }
  return userSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((u) => !u.dummyTeam)
    // Already a person — by the uid LINK first, the only exact join, then by
    // email, which is how linkStaffToAccounts matches.
    .filter((u) => !linkedUids.has(u.id))
    .filter((u) => {
      const em = String(u.email || "").trim().toLowerCase();
      return !em || !linkedEmails.has(em);
    });
};

export const listFacilityRoster = async (facilityId) => {
  const snap = await getDocs(
    query(staffCol(), where("primaryFacilityId", "==", facilityId))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    // The dummy team is a test fixture, not a colleague to borrow.
    .filter((p) => !p.dummyTeam)
    .map((p) => ({
      ...p,
      // staff says doctor | crna | aa | resident; the picker branches on the
      // users vocabulary. One translation, here, rather than a second role
      // vocabulary spreading through the component.
      role: p.role === "doctor" ? "anesthesiologist" : "provider",
      subRole: p.role === "doctor" ? null : p.role || null,
      facilityIds: p.primaryFacilityId ? [p.primaryFacilityId] : [],
    }));
};

// Flag a roster member as departed for the day. Routed through setDailyRoster
// (merge:true), so roomOrder/notes and other departed entries are preserved —
// `departed` is a map of memberId → boolean on the daily board doc.
// Stamp the moment a provider became UNASSIGNED, so the board can say how long
// they have been free rather than guessing.
//
// Mirrors setRosterMemberDeparted exactly: a map of memberId -> time on the
// daily board, written through setDailyRoster (merge:true) so other entries and
// roomOrder/notes survive. No rules change is needed — daily_boards update has
// no field allowlist, only the aicId lock.
//
// ⚠️ SERVER TIME, NOT THE CLIENT'S. A wall display with a skewed clock would
// otherwise show every provider a different duration, and two devices would
// disagree about the same person. serverTimestamp() nested in a merge write is
// supported and is the only value all readers agree on.
//
// ⚠️ ABSENCE IS MEANINGFUL AND MUST NOT BE BACKFILLED. A provider who was never
// assigned today has no stamp, and the board shows TBD rather than inventing a
// start. Stamping them on first sight would claim we observed something we did
// not.
export const setRosterMemberFreeSince = (facilityId, date, memberId, updatedBy) =>
  setDailyRoster(
    facilityId,
    date,
    { freeSince: { [memberId]: serverTimestamp() } },
    updatedBy
  );

export const setRosterMemberDeparted = (facilityId, date, memberId, departed, updatedBy) =>
  setDailyRoster(facilityId, date, { departed: { [memberId]: !!departed } }, updatedBy);

// GIVING BREAKS — the provider stays on the bench, doing the break rounds.
//
// ⚠️ A MAP ON THE ROSTER, NOT A ROOM ASSIGNMENT, AND NOT `departed`. Giving
// breaks is the one placement that must leave the person exactly where they
// are: they are still here, still callable, and the board must keep counting
// them as free — an assignment row would hide them from the bench and a
// `departed` flag would move them to Home, which is how someone becomes
// invisible (the failure BenchSections was written to prevent). So it is a
// LABEL on a bench member, stored the way `departed` is: a memberId -> boolean
// map merged into the day's roster, so other entries survive the write and no
// rules change is needed (daily_boards update carries no field allowlist on
// the in-facility branch).
//
// ⚠️ IT MUST BE REVERSIBLE FROM THE SAME PLACE IT IS SET. Break rounds end;
// a flag with no off switch would leave the board asserting a duty that
// finished hours ago, and the only cure would be a database edit.
export const setRosterMemberGivingBreaks = (facilityId, date, memberId, giving, updatedBy) =>
  setDailyRoster(facilityId, date, { givingBreaks: { [memberId]: !!giving } }, updatedBy);

// ── MD CALL removal sync (TV mode) ────────────
// The MD call list is FACILITY-WIDE (one sheet feeds all campuses), so removing
// a doctor from call must land on every board at once. Each helper walks the
// ACTIVE facilities and rewrites that board's mdCallList/mdCallHome inside a
// transaction. Best-effort per facility: a permission-denied on another campus
// never blocks the local removal — board managers and admins sync all boards
// (rules: field-scoped cross-facility write for boardmanager).

// The facility ids to sync across, derived LIVE from the facilities collection
// (blocker #3 fix) so a newly-added campus is covered without a code change.
// Resilient: any read failure falls back to the known cores, so cross-board
// sync degrades to today's exact behaviour rather than breaking.
async function syncFacilityIds() {
  try {
    const snap = await getDocs(facilitiesCol());
    return activeFacilityIds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  } catch (err) {
    console.warn("[mdCallSync] facilities read failed, using core fallback:", err?.code || err);
    return [...CORE_FACILITY_IDS];
  }
}

async function forEachBoard(date, fn) {
  const results = [];
  const facilityIds = await syncFacilityIds();
  for (const fid of facilityIds) {
    try {
      const ref = doc(db, "daily_boards", boardId(fid, date));
      // TRANSACTION (race fix): the roster/call-list arrays live whole on this
      // one shared doc, and multiple clients (every open TV + board manager) can
      // fire these syncs at the same instant. A plain getDoc→compute→updateDoc
      // read-modify-write let a concurrent add between the read and the write be
      // clobbered (a doctor re-added to mdCallList vanished when another device's
      // removal wrote a stale array back). runTransaction re-reads inside the
      // transaction and retries on conflict, so each sync composes against fresh
      // data instead of a stale snapshot. One board = one independent txn.
      const wrote = await runTransaction(db, async (txn) => {
        const snap = await txn.get(ref);
        if (!snap.exists()) return false; // no board = nothing to sync there
        const write = fn(snap.data(), fid);
        if (!write) return false;
        txn.update(ref, write);
        return true;
      });
      if (wrote) results.push(fid);
    } catch (err) {
      console.warn(`[mdCallSync] ${fid} skipped:`, err?.code || err);
    }
  }
  return results;
}

// X pressed: move the doctor (by sheet code) from mdCallList → mdCallHome on
// every facility board that lists them. entry = { code, name }.
export const syncMdCallRemoval = (date, entry, updatedBy) =>
  forEachBoard(date, (data) => {
    const list = Array.isArray(data.mdCallList) ? data.mdCallList : [];
    const found = list.find((e) => e.code === entry.code);
    if (!found) return null;
    const home = (Array.isArray(data.mdCallHome) ? data.mdCallHome : []).filter((e) => e.code !== entry.code);
    return {
      mdCallList: list.filter((e) => e.code !== entry.code),
      mdCallHome: [...home, { ...found, name: entry.name || found.name || entry.code }],
      updatedBy: updatedBy ?? null,
      updatedAt: serverTimestamp(),
    };
  });

// Back / Cancel: restore the doctor from mdCallHome → mdCallList everywhere.
export const syncMdCallRestore = (date, code, updatedBy) =>
  forEachBoard(date, (data) => {
    const home = Array.isArray(data.mdCallHome) ? data.mdCallHome : [];
    const entry = home.find((e) => e.code === code);
    if (!entry) return null;
    const list = (Array.isArray(data.mdCallList) ? data.mdCallList : []).filter((e) => e.code !== code);
    return {
      mdCallList: [...list, entry],
      mdCallHome: home.filter((e) => e.code !== code),
      updatedBy: updatedBy ?? null,
      updatedAt: serverTimestamp(),
    };
  });

// Confirm Home: drop the pending entry everywhere; when the doctor is a
// rostered person (staffId resolved via shortCode) also remove them from that
// board's supervisor/CRNA roster lists so every roster box lets them go.
export const syncMdCallConfirmHome = (date, code, staffId, updatedBy) =>
  forEachBoard(date, (data) => {
    const home = Array.isArray(data.mdCallHome) ? data.mdCallHome : [];
    const inHome = home.some((e) => e.code === code);
    const supervisors = Array.isArray(data.supervisors) ? data.supervisors : [];
    const crnas = Array.isArray(data.crnas) ? data.crnas : [];
    const onRoster = staffId && (supervisors.some((s) => s.id === staffId) || crnas.some((c) => c.id === staffId));
    if (!inHome && !onRoster) return null;
    return {
      mdCallHome: home.filter((e) => e.code !== code),
      ...(staffId
        ? {
            supervisors: supervisors.filter((s) => s.id !== staffId),
            crnas: crnas.filter((c) => c.id !== staffId),
          }
        : {}),
      updatedBy: updatedBy ?? null,
      updatedAt: serverTimestamp(),
    };
  });

// Locum leaving time (Timeline page Locum List): lock the "HH:MM" onto every
// facility board (the MD call list is facility-wide, so the staffing math on
// every campus must see the same leave).
export const setLocumLeaveTime = (date, code, hm, updatedBy) =>
  forEachBoard(date, () => ({
    [`locumLeaveTimes.${code}`]: hm,
    updatedBy: updatedBy ?? null,
    updatedAt: serverTimestamp(),
  }));

// Locum departure at the locked time: remove them from the MD call list
// OUTRIGHT on every board (no Home-box pending — the leave was scheduled),
// and clear the stored leaving time. Idempotent: boards already without the
// code are skipped, so multiple open clients firing the timer is harmless.
export const syncLocumDeparture = (date, code, updatedBy) =>
  forEachBoard(date, (data) => {
    const list = Array.isArray(data.mdCallList) ? data.mdCallList : [];
    if (!list.some((e) => e.code === code)) return null;
    return {
      mdCallList: list.filter((e) => e.code !== code),
      [`locumLeaveTimes.${code}`]: deleteField(),
      updatedBy: updatedBy ?? null,
      updatedAt: serverTimestamp(),
    };
  });

// Confirm-home: remove a member from today's roster entirely (drops them from
// the Unassigned/Home boxes and the assignable pool). Filters the member out of
// both crnas[] and supervisors[] and clears their `departed` flag. Read the live
// board doc first so we rewrite the arrays without them; if there's no board doc
// yet there's nothing to remove. Works for CRNAs and doctors (AIC excepted — the
// active AIC isn't a supervisors[] entry).
export const removeRosterMember = async (facilityId, date, memberId, updatedBy) => {
  const ref = doc(db, "daily_boards", boardId(facilityId, date));
  // TRANSACTION (race fix): crnas[]/supervisors[] are whole arrays on the shared
  // board doc. A getDoc→filter→updateDoc read-modify-write dropped a concurrent
  // roster add made between the read and the write. Re-reading inside the txn
  // (auto-retry on conflict) rebuilds the arrays from fresh data each attempt.
  return runTransaction(db, async (txn) => {
    const snap = await txn.get(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    const crnas = (data.crnas || []).filter((c) => c.id !== memberId);
    const supervisors = (data.supervisors || []).filter((s) => s.id !== memberId);
    txn.update(ref, {
      crnas,
      supervisors,
      [`departed.${memberId}`]: deleteField(),
      updatedBy: updatedBy ?? null,
      updatedAt: serverTimestamp(),
    });
    return true;
  });
};

// ── BREAKS ────────────────────────────────────
// LOCAL day key (YYYY-MM-DD) — must match rosterDateKey so a break filed in the
// evening lands on the same OR day as the roster/board (UTC stamped it under
// tomorrow). Inlined here to avoid a hooks->firestore import cycle.
const localDayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const requestBreak = (data) => {
  return addDoc(breaksCol(), {
    ...data,
    status: "requested",
    requestedAt: serverTimestamp(),
    date: localDayKey(),
  });
};

export const updateBreak = (breakId, data) => {
  return updateDoc(doc(db, "breaks", breakId), data);
};

// Phase 5.3 — the requester's own break docs (equality-only query, no
// composite index; caller filters to today's dateKey client-side like
// useBreaks does).
export const listenToMyBreaks = (facilityId, providerId, callback, onError) =>
  onSnapshot(
    query(
      breaksCol(),
      where("facilityId", "==", facilityId),
      where("providerId", "==", providerId)
    ),
    callback,
    onError
  );

// Phase 5.3 — approval starts the break window in one atomic batch:
// the /breaks record flips to approved WITH named coverage (C-003 giver/
// receiver mapping) and the room doc opens the countdown window + the
// engine channel (caller passes the already-transitioned breaks map).
export const startApprovedBreak = ({
  breakId,
  roomId,
  breaks,
  channel,
  coverId,
  coverName,
  minutes,
  approverId,
}) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "breaks", breakId), {
    status: "approved",
    approvedAt: serverTimestamp(),
    approvedBy: approverId || null,
    receiverId: coverId || null,
    receiverName: coverName || "",
    estimatedMinutes: minutes ?? null,
    startedAt: new Date().toISOString(),
  });
  if (roomId) {
    batch.update(doc(db, "rooms", roomId), {
      breaks,
      relieverId: coverId || null,
      relieverName: coverName || "",
      onBreak: true,
      breakChannel: channel || null,
      breakStartedAt: new Date().toISOString(),
      breakMinutes: minutes ?? null,
      activeBreakId: breakId,
      updatedAt: serverTimestamp(),
    });
  }
  return batch.commit();
};

// NOTE: no date filter here — adding where("date","==",...) alongside the
// status `in` clause would require a composite index (facilityId+date+status)
// that isn't deployed (same index-avoidance convention as the alert/DM
// listeners). useBreaks filters the snapshot to today's local day key
// client-side, which drops yesterday's un-actioned requests.
export const listenToPendingBreaks = (facilityId, callback, onError) =>
  onSnapshot(
    query(
      breaksCol(),
      where("facilityId", "==", facilityId),
      where("status", "in", ["requested", "finding_relief"])
    ),
    callback,
    onError
  );

// ── ALERTS ────────────────────────────────────
export const sendAlert = (data) => {
  return addDoc(alertsCol(), {
    ...data,
    status: "sent",
    sentAt: serverTimestamp(),
    acknowledgedBy: [],
  });
};

// NOTE: no orderBy here — combining facilityId== + status in + orderBy(sentAt)
// requires a composite index that isn't deployed. Equality-only needs none.
// Consumers sort by sentAt client-side (alert volume is tiny).
export const listenToAlerts = (facilityId, callback, onError) =>
  onSnapshot(
    query(
      alertsCol(),
      where("facilityId", "==", facilityId),
      where("status", "in", ["sent", "acknowledged"])
    ),
    callback,
    onError
  );

// A recipient taps Acknowledge — PER-USER: only this user is added to
// acknowledgedBy, which clears the alert from THEIR view (popup + their room
// card) while every other target still sees it. We deliberately do NOT flip a
// shared `status: "acknowledged"` here — that would clear the alert for everyone
// and the board would stop signalling after a single ack. The alert stays live
// (status "sent") and the board keeps blinking until someone RESOLVES it.
export const acknowledgeAlert = (alertId, uid) =>
  updateDoc(doc(db, "alerts", alertId), {
    acknowledgedBy: arrayUnion(uid),
    acknowledgedAt: serverTimestamp(),
  });

// The supervising doctor (or AIC/admin) sounds the all-clear. Resolving drops
// the alert from the live listener query, so the board returns to normal.
export const resolveAlert = (alertId, uid) =>
  updateDoc(doc(db, "alerts", alertId), {
    status: "resolved",
    resolvedBy: uid,
    resolvedAt: serverTimestamp(),
  });

// ── TASK DIRECTIVES ──────────────────────────
// The board→phone one-way lane. Shaped like alerts (facility-scoped, targeted,
// confirmed per-user) but deliberately a SEPARATE collection:
//   • the alerts listener is live-only (status in sent/acknowledged), so a
//     confirmed directive would vanish from the dashboard whose whole job is to
//     show it as confirmed;
//   • alerts drive the red/blink board visuals, and routine room churn must
//     never reach that layer.
// Pure logic (status, sorting, ledger, payload validation) lives in
// src/domain/tasks.js.
//
// ⚠️ A DIRECTIVE COMPOSED INSIDE A REHEARSAL MUST NOT RING A REAL PHONE.
// `TEST_SESSION_UNCOPIED` refuses to COPY task history into a session, and
// functions/taskPush refuses to push anything stamped `isTestData` — two
// defences that between them cover the seed and nothing else. A directive
// TYPED during a rehearsal was covered by neither: joining a session sets
// `activeFacilityId` to `tst-<code>`, so the composer's own block check passes;
// the seeder deliberately keeps REAL staff ids (that is what puts real names on
// a rehearsal board), so `uidOf` resolves them to real auth uids; and this
// function never marked the document. An admin rehearsing at 21:00 could pick
// the CRNA the copied board shows in WOR 6, press Send, and buzz that
// clinician's phone at home with "Take over from Jim Wright." — indistinguish-
// able from a real order.
//
// Stamped HERE rather than at each call site: this is the one door every
// manual directive goes through, and a rule that has to be remembered per
// caller is a rule that will be missed by the next one.
export const createTask = (data) => {
  const testFacility = isTestFacilityId(data?.facilityId);
  return addDoc(tasksCol(), {
    ...data,
    ...(testFacility ? { isTestData: true } : null),
    createdAt: serverTimestamp(),
    confirmations: {},
  });
};

// Equality-only, like listenToAlerts and for the same reason: adding
// orderBy(createdAt) to a two-field equality query needs a composite index that
// is not deployed. Consumers sort with domain/tasks.sortTasksNewestFirst.
//
// Scoped by BOTH facility and day. Facility alone would leak another campus's
// directives; day alone would resurrect yesterday's "go home" every morning.
export const listenToTasks = (facilityId, dateKey, callback, onError) =>
  onSnapshot(
    query(
      tasksCol(),
      where("facilityId", "==", facilityId),
      where("dateKey", "==", dateKey)
    ),
    callback,
    onError
  );

// A recipient taps "Got it" — an awareness receipt, NOT a completion claim.
//
// `confirmations` is a MAP keyed by uid rather than an array of {uid, at}
// objects, because a serverTimestamp cannot live inside an array element. With
// a map the stamp is the SERVER's, so "seen 3:42 PM" does not depend on the
// phone's clock being right; and firestore.rules can enforce that a user may
// add exactly their own key, which arrayUnion of objects cannot express.
export const confirmTask = (taskId, uid) =>
  updateDoc(doc(db, "tasks", taskId), {
    [`confirmations.${uid}`]: serverTimestamp(),
  });

// The longest a decline reason may be. Mirrors the rule in firestore.rules,
// which is the half a bypassed client cannot skip — this one only spares an
// honest caller a refusal they cannot read.
export const MAX_DECLINE_REASON = 280;

// A recipient taps "Can't right now" and says why.
//
// ⚠️ UNTIL THIS EXISTED, "GOT IT" WAS THE ONLY REPLY A PHONE COULD MAKE. A
// directive landing during an induction, an emergence, a prone flip or an open
// abdomen could only be IGNORED — and the board cannot tell ignoring apart from
// not having seen it, from having no signal, from being scrubbed in. Silence
// meant four different things and the board had to guess at which. A decline
// with a reason means exactly one, and it means it immediately.
//
// A MAP, not a timestamp, because a refusal without a reason is barely better
// than the silence: "not now" and "I am in an open abdomen for another hour"
// lead the board to do completely different things. `at` is the SERVER's clock
// for the same reason `confirmations` is — and because the rule pins it, a
// decline can be neither back-dated nor quietly withdrawn.
export const declineTask = (taskId, uid, reason) => {
  const why = String(reason || "").trim().slice(0, MAX_DECLINE_REASON);
  // Refusing with no reason is the silence this exists to replace, so the
  // caller is stopped here rather than being refused by the rule.
  if (!why) throw new Error("A decline needs a reason — say why so the board can act on it.");
  return updateDoc(doc(db, "tasks", taskId), {
    [`declines.${uid}`]: { at: serverTimestamp(), reason: why },
  });
};

// ── MESSAGES ─────────────────────────────────
export const sendMessage = (data) => {
  return addDoc(messagesCol(), {
    ...data,
    isRead: false,
    sentAt: serverTimestamp(),
  });
};

// Delete a whole DM conversation between the current user and one contact (the
// X on a DIRECT MESSAGES row). Firestore has no OR query, so fetch both legs
// (mine→them, them→mine) and delete each doc. The rule allows delete only when
// the caller is the sender or recipient. Best-effort; caller swallows errors.
export const deleteDmThread = async (uid, contactId) => {
  if (!uid || !contactId) return;
  const [sent, received] = await Promise.all([
    getDocs(query(messagesCol(), where("senderId", "==", uid), where("recipientId", "==", contactId))),
    getDocs(query(messagesCol(), where("senderId", "==", contactId), where("recipientId", "==", uid))),
  ]);
  await Promise.all(
    [...sent.docs, ...received.docs].map((d) => deleteDoc(d.ref).catch(() => {}))
  );
};

// Best-effort midnight purge of the user's DM docs older than the day cutoff
// (UTC start of today). Two equality legs (sent + received), filtered by sentAt
// client-side. The rule lets sender/recipient delete. A pending serverTimestamp
// (sentAt null) is treated as "today" and kept.
export const deleteStaleDms = async (uid, cutoffMillis) => {
  if (!uid) return;
  const [sent, received] = await Promise.all([
    getDocs(query(messagesCol(), where("senderId", "==", uid))),
    getDocs(query(messagesCol(), where("recipientId", "==", uid))),
  ]);
  const seen = new Set();
  const stale = [...sent.docs, ...received.docs].filter((d) => {
    if (seen.has(d.id)) return false;
    seen.add(d.id);
    const ms = d.data().sentAt?.toMillis?.() ?? null;
    return ms !== null && ms < cutoffMillis;
  });
  await Promise.all(stale.map((d) => deleteDoc(d.ref).catch(() => {})));
};

// A supervising doctor "has limited access" and "must notify the AIC of any
// changes made". This sends an in-app message to every AIC in the facility
// summarising the change. Best-effort: a notification failure must never roll
// back the change itself, so call sites swallow its errors.
export const notifyAIC = async ({ facilityId, actor, summary }) => {
  const aics = await getFacilityAICs(facilityId);
  await Promise.all(
    aics
      .filter((a) => a.id !== actor?.uid) // don't notify yourself if you are AIC
      .map((a) =>
        sendMessage({
          facilityId,
          senderId: actor?.uid || null,
          senderName: actor?.name || "A supervising doctor",
          recipientId: a.id,
          recipientName: a.name || "AIC",
          type: "board_change",
          message: `🩺 ${actor?.name || "A supervising doctor"} ${summary}`,
        })
      )
  );
};

// NOTE: no orderBy — combining where(recipientId==) + orderBy(sentAt) needs a
// composite index that isn't deployed (live failed-precondition once the global
// MessagingProvider mounts this on every page). The only caller (MessagingContext)
// merges the received + sent legs and sorts by sentAt client-side, so server-side
// ordering is redundant. Same convention as listenToChannel + the sent-leg listener.
export const listenToMessages = (userId, callback) =>
  onSnapshot(
    query(messagesCol(), where("recipientId", "==", userId)),
    callback
  );

// ── BROADCASTS (TV-mode AIC announcements) ────
// Day-scoped announcement messages shown on the TV board's Broadcast panel.
// ONLY the day's AIC (or an admin) may write/edit/delete — enforced by the
// security rules via the daily_boards aicId, so write access auto-transfers when
// the AIC is reassigned. Read is facility-wide. No composite index: query by
// facilityId, filter dateKey + sort client-side (broadcast volume is tiny).
export const broadcastsCol = () => collection(db, "broadcasts");

export const addBroadcast = ({ facilityId, dateKey, authorId, authorName, text }) =>
  addDoc(broadcastsCol(), {
    facilityId: facilityId ?? null,
    dateKey: dateKey ?? null,
    authorId: authorId ?? null,
    authorName: authorName ?? null,
    text: text || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const updateBroadcast = (id, text) =>
  updateDoc(doc(db, "broadcasts", id), { text: text || "", updatedAt: serverTimestamp() });

export const deleteBroadcast = (id) => deleteDoc(doc(db, "broadcasts", id));

export const listenToBroadcasts = (facilityId, dateKey, callback) =>
  onSnapshot(
    query(broadcastsCol(), where("facilityId", "==", facilityId)),
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((b) => (b.dateKey || null) === dateKey)
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      callback(rows);
    },
    (err) => console.error("listenToBroadcasts failed:", err)
  );

// ── CHANNEL MESSAGING ─────────────────────────
// Real-time group/broadcast chat (Phase 5) lives in its own module to keep this
// file under the 500-line budget. Re-exported here so callers keep a single
// `from "../firebase/firestore"` import surface (alerts/DM convention).
export {
  channelsCol,
  channelMessagesCol,
  materializeChannel,
  sendChannelMessage,
  markChannelMessageRead,
  listenToChannel,
  createCustomChannel,
  addChannelMembers,
  listenToMyChannels,
  listenToAllChannelsForDay,
  deleteCustomChannel,
  deleteStaleChannelMessages,
} from "./channels";

// ── AUDIT LOG ─────────────────────────────────
// Only written by Cloud Functions — this is read-only from the client
export const listenToAuditLog = (facilityId, callback) =>
  onSnapshot(
    query(
      auditCol(),
      where("facilityId", "==", facilityId),
      orderBy("timestamp", "desc")
    ),
    callback
  );
